import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, PersonOutline } from "@mui/icons-material";
import { assignCaseArbitrator, getArbitrators, getCaseStage, setCaseStage } from "../api/caseStage";
import useErrorHandler from "../hooks/useErrorHandler";
import useSnackbar from "../hooks/useSnackbar";

const whenFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

// Small dialog shell used for advance (optional note), move back (required reason) and arbitrator assignment.
function StageDialog({ open, title, confirmLabel, children, busy, disabled, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 pt-2">{children}</Box>
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy || disabled}>
          {busy ? "Saving..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// onChanged: called after any successful change so the host page can refresh (the case status may change).
export default function CaseWorkflow({ caseId, onChanged }) {
  const { data: user } = useSelector((state) => state.user);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();

  const [info, setInfo] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState(null); // 'advance' | 'back' | 'arbitrator'
  const [note, setNote] = useState("");
  const [backTo, setBackTo] = useState("");
  const [arbitrators, setArbitrators] = useState(null);
  const [arbitratorId, setArbitratorId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    if (!user?.token || !caseId) return Promise.resolve();
    return getCaseStage(user.token, caseId)
      .then(({ data }) => {
        setInfo(data.data);
        setFailed(false);
      })
      .catch((err) => {
        setFailed(true);
        errorHandler(err);
      });
  }, [user?.token, caseId]);

  useEffect(() => {
    setInfo(null);
    load();
  }, [load]);

  const close = () => {
    setDialog(null);
    setNote("");
    setBackTo("");
    setArbitratorId("");
  };

  // Runs a mutation, then refreshes both the stage data and the host page.
  const run = async (request, message) => {
    setBusy(true);
    try {
      const { data } = await request();
      setInfo(data.data);
      snackbar.showSuccess(message);
      close();
      if (onChanged) onChanged();
    } catch (err) {
      errorHandler(err);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const openArbitratorDialog = () => {
    setArbitratorId(info?.arbitrator?.id ? String(info.arbitrator.id) : "");
    setDialog("arbitrator");
    if (arbitrators === null) {
      getArbitrators(user.token)
        .then(({ data }) => setArbitrators(data.data || []))
        .catch((err) => {
          setArbitrators([]);
          errorHandler(err);
        });
    }
  };

  if (failed && !info) {
    return (
      <Paper className="odr-card mb-4">
        <Typography className="p-4 text-center text-[#6b7a90]">Case stage couldn't be loaded.</Typography>
      </Paper>
    );
  }
  if (!info) {
    return (
      <Paper className="odr-card mb-4">
        <Box className="py-8 grid place-items-center">
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  const keys = info.stages.map((s) => s.key);
  const currentIdx = keys.indexOf(info.current);
  const closed = info.current === "CLOSED";
  const earlier = info.stages.slice(0, Math.max(currentIdx, 0));
  const labelOf = (key) => info.stages.find((s) => s.key === key)?.label || key;

  return (
    <Paper className="odr-card mb-4">
      <Box className="odr-card-header flex flex-wrap items-center justify-between gap-2">
        <Typography className="heading-title">Case Workflow</Typography>
        <Typography className="text-[13px] text-[#6b7a90]">
          Current stage: <span className="font-semibold text-brand">{info.currentLabel}</span>
        </Typography>
      </Box>

      <Box className="px-4 pb-4">
        <Box className="overflow-x-auto pb-2">
          <Stepper activeStep={closed ? keys.length : currentIdx} alternativeLabel className="min-w-[560px]">
            {info.stages.map((s) => (
              <Step key={s.key}>
                <StepLabel>
                  <span className={s.key === info.current ? "font-bold text-brand" : ""}>{s.label}</span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <Box className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
            <Box className="flex items-center gap-2 min-w-0">
              <PersonOutline className="text-[#6b7a90]" />
              <Box className="min-w-0">
                <Typography className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#6b7a90]">Arbitrator</Typography>
                {info.arbitrator ? (
                  <Typography className="text-[14px] text-[#0d1b36] break-words">
                    <span className="font-semibold">{info.arbitrator.name}</span>
                    <span className="text-[#6b7a90] break-all"> · {info.arbitrator.email}</span>
                  </Typography>
                ) : (
                  <Typography className="text-[14px] text-[#6b7a90]">No arbitrator assigned</Typography>
                )}
              </Box>
            </Box>
            {info.canAssignArbitrator ? (
              <Button size="small" variant="outlined" onClick={openArbitratorDialog}>
                {info.arbitrator ? "Change arbitrator" : "Assign arbitrator"}
              </Button>
            ) : null}
          </Box>

          <Box className="flex flex-wrap items-center gap-2">
            {info.canRevert ? (
              <Button variant="outlined" onClick={() => setDialog("back")}>
                Move back
              </Button>
            ) : null}
            {info.canAdvance ? (
              <Button variant="contained" onClick={() => setDialog("advance")}>
                Advance to {info.nextLabel}
              </Button>
            ) : info.blockedReason && info.nextLabel ? (
              <Button variant="contained" disabled>
                Advance to {info.nextLabel}
              </Button>
            ) : null}
          </Box>
        </Box>

        {!info.canAdvance && info.blockedReason ? (
          <Typography className="mt-2 text-[13px] text-[#b45309] text-right max-[599px]:text-left">{info.blockedReason}</Typography>
        ) : null}

        <Box className="mt-3 border-t border-solid border-t-[#e6eaf2] pt-2">
          <button
            type="button"
            onClick={() => setShowHistory((o) => !o)}
            aria-expanded={showHistory}
            className="flex items-center gap-1 p-0 text-[13px] font-semibold text-brand bg-transparent border-0 cursor-pointer"
          >
            {showHistory ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            Stage history ({info.history.length})
          </button>
          <Collapse in={showHistory} unmountOnExit>
            {info.history.length ? (
              <ul className="m-0 mt-2 p-0 list-none">
                {info.history.map((h) => (
                  <li key={h.id} className="py-2 border-b border-solid border-b-[#eef1f6] last:border-b-0">
                    <Box className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                      <Typography className="text-[14px] font-semibold text-[#0d1b36]">
                        {labelOf(h.stage)}
                        {h.entered_by_name ? <span className="font-normal text-[#6b7a90]"> · {h.entered_by_name}</span> : null}
                      </Typography>
                      <Typography className="text-[12px] text-[#6b7a90] whitespace-nowrap">{whenFmt.format(new Date(h.entered_at))}</Typography>
                    </Box>
                    {h.note ? <Typography className="mt-0.5 text-[13px] text-[#344767] break-words">{h.note}</Typography> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <Typography className="py-2 text-[13px] text-[#6b7a90]">No stage changes recorded yet.</Typography>
            )}
          </Collapse>
        </Box>
      </Box>

      <StageDialog
        open={dialog === "advance"}
        title={`Advance to ${info.nextLabel || ""}`}
        confirmLabel="Advance"
        busy={busy}
        onClose={close}
        onConfirm={() => run(() => setCaseStage(user.token, caseId, { stage: info.next, note: note.trim() || undefined }), `Case moved to ${info.nextLabel}`)}
      >
        <Typography className="text-[14px]">
          Move this case from {info.currentLabel} to {info.nextLabel}?
          {info.next === "CLOSED" ? " Closing the case also sets its status to Closed." : ""}
        </Typography>
        <TextField size="small" label="Note (optional)" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </StageDialog>

      <StageDialog
        open={dialog === "back"}
        title="Move case back"
        confirmLabel="Move back"
        busy={busy}
        disabled={!backTo || !note.trim()}
        onClose={close}
        onConfirm={() => run(() => setCaseStage(user.token, caseId, { stage: backTo, note: note.trim() }), `Case moved back to ${labelOf(backTo)}`)}
      >
        <FormControl size="small">
          <InputLabel id="wf-back-stage">Move back to</InputLabel>
          <Select labelId="wf-back-stage" label="Move back to" value={backTo} onChange={(e) => setBackTo(e.target.value)}>
            {earlier.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Reason" required multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </StageDialog>

      <StageDialog
        open={dialog === "arbitrator"}
        title={info.arbitrator ? "Change arbitrator" : "Assign arbitrator"}
        confirmLabel="Assign"
        busy={busy}
        disabled={!arbitratorId || arbitratorId === String(info.arbitrator?.id || "")}
        onClose={close}
        onConfirm={() => run(() => assignCaseArbitrator(user.token, caseId, Number(arbitratorId)), "Arbitrator assigned")}
      >
        {arbitrators === null ? (
          <Box className="py-4 grid place-items-center">
            <CircularProgress size={22} />
          </Box>
        ) : !arbitrators.length ? (
          <Typography className="text-[14px] text-[#6b7a90]">No arbitrator accounts yet — create a user with the Arbitrator role.</Typography>
        ) : (
          <FormControl size="small">
            <InputLabel id="wf-arb">Arbitrator</InputLabel>
            <Select labelId="wf-arb" label="Arbitrator" value={arbitratorId} onChange={(e) => setArbitratorId(e.target.value)}>
              {arbitrators.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.name} ({a.email})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Only users with the Arbitrator role are listed.</FormHelperText>
          </FormControl>
        )}
      </StageDialog>
    </Paper>
  );
}
